// V2.10 — Landing social proof banner.
// Live stats from GET /api/public-stats (15 min cache server-side).
// - Count-up animation with requestAnimationFrame (no framer-motion dependency).
// - `low_data=true` → soft "coming soon" teaser (avoids the demoralising "0 signed").
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FileCheck, Zap, Users, Wallet, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

function useCountUp(target, ms = 900) {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (target == null) return;
    const start = performance.now();
    const from = 0;
    const to = Number(target) || 0;
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / ms);
      const eased = 1 - (1 - t) * (1 - t); // easeOut quad
      setV(from + (to - from) * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, ms]);
  return v;
}

function fmtInt(n) {
  return Math.round(Number(n) || 0).toLocaleString("fr-FR");
}
function fmtEur(n) {
  return `${Math.round(Number(n) || 0).toLocaleString("fr-FR")} €`;
}
function fmtGwh(kwh) {
  const gwh = Number(kwh || 0) / 1_000_000;
  if (gwh >= 1) return `${gwh.toFixed(1)} GWh`;
  const mwh = Number(kwh || 0) / 1000;
  if (mwh >= 1) return `${mwh.toFixed(1)} MWh`;
  return `${Math.round(Number(kwh) || 0)} kWh`;
}

export default function SocialProofBanner({ compact = false, className = "" }) {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/public-stats");
        if (!cancelled) setStats(data);
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const signedQuotes = useCountUp(stats?.total_signed_quotes || 0);
  const customers = useCountUp(stats?.total_customers || 0);
  const kwh = useCountUp(stats?.total_kwh_installed || 0);
  const savings = useCountUp(stats?.avg_customer_savings_eur || 0);

  const showTeaser = useMemo(
    () => !stats || stats.low_data || error,
    [stats, error],
  );

  if (showTeaser) {
    return (
      <div
        data-testid="social-proof-teaser"
        className={`w-full border-y border-emerald-500/20 bg-gradient-to-r from-emerald-500/5 via-emerald-500/10 to-blue-500/5 py-3 ${className}`}
      >
        <div className="mx-auto flex max-w-6xl items-center justify-center gap-2 px-4 text-center text-sm text-emerald-900">
          <Sparkles className="h-4 w-4 flex-none text-emerald-500" />
          <span data-testid="social-proof-coming-soon">
            {t("landing.social_proof.coming_soon")}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="social-proof-banner"
      className={`w-full border-y border-emerald-500/20 bg-gradient-to-r from-emerald-500/10 to-blue-500/10 py-3 ${className}`}
    >
      <div
        className={`mx-auto grid max-w-6xl gap-x-6 gap-y-3 px-4 text-sm text-slate-700 ${
          compact ? "grid-cols-2 md:grid-cols-4" : "grid-cols-2 md:grid-cols-4"
        }`}
      >
        <Stat
          testId="social-signed-quotes"
          icon={FileCheck}
          value={fmtInt(signedQuotes)}
          label={t("landing.social_proof.signed_quotes")}
        />
        <Stat
          testId="social-total-kwh"
          icon={Zap}
          value={fmtGwh(kwh)}
          label={t("landing.social_proof.total_production_gwh")}
        />
        <Stat
          testId="social-customers"
          icon={Users}
          value={fmtInt(customers)}
          label={t("landing.social_proof.customers")}
        />
        <Stat
          testId="social-avg-savings"
          icon={Wallet}
          value={fmtEur(savings)}
          label={t("landing.social_proof.avg_savings")}
        />
      </div>
    </div>
  );
}

function Stat({ testId, icon: Icon, value, label }) {
  return (
    <div
      data-testid={testId}
      className="flex min-w-0 items-center gap-2 md:justify-center"
    >
      <Icon className="h-4 w-4 flex-none text-emerald-600" strokeWidth={2.2} />
      <span className="whitespace-nowrap">
        <span className="font-semibold text-emerald-900 tabular-nums">{value}</span>
        <span className="ml-1 text-slate-600">{label}</span>
      </span>
    </div>
  );
}
